import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { Map } from 'immutable';
import queryString from 'query-string';
import classNames from 'classnames';

// hooks
import { useSelector, useDispatch } from 'react-redux';
import { usePrevious } from 'helpers/hooks/usePrevious';

// action creators
import { setCurrentGroup } from 'store/actions/groupActions';
import { stopListeningForThreadsAction } from 'store/actions/threadActions';
import { stopListeningForMessagesAction } from 'store/actions/messageActions';
import { fetchStripeConfig } from 'store/actions/billingActions';
import { fetchCurrentUser } from 'store/actions/userActions';

// react router
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';

// MUI components
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Hidden from '@material-ui/core/Hidden';
import Toolbar from '@material-ui/core/Toolbar';
import Box from '@material-ui/core/Box';

// components
import AppHeader from 'layout/ApplicationLayout/components/AppHeader';
import MobileMenu from 'routes/MobileMenu';
import MobileNavigation from 'layout/MobileNavigation';
import Sidebar from 'layout/Sidebar';
import ErrorBoundary from 'layout/ErrorBoundary';

// helpers
import getPages from 'layout/ApplicationLayout/helpers/getPages';
import initializeZohoSupport, {
  ZOHO_SUPPORT_ID,
} from './helpers/initializeZohoSupport';

// selectors
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
import getCurrentUserGroups from 'store/selectors/getCurrentUserGroups';
import getFeatures from 'store/selectors/getFeatures';
import getCurrentSubscription from 'store/selectors/getCurrentSubscription';

const ApplicationLayout = function({
  isSystemAdmin,
  isHqAdmin,
  isCouncilAdmin,
  isCurrentGroupChapter,
  isCurrentGroupOrganization,
  isCurrentGroupCouncil,
  isCurrentGroupSystem,
}: any) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const previousPathname = usePrevious(pathname);

  const {
    currentGroupId,
    currentSubscription,
    features,
    groupIds,
    permissions,
    roleId,
  } = useSelector(
    state => ({
      currentGroupId: getCurrentGroupId(state),
      groupIds: getCurrentUserGroups(state),
      features: getFeatures(state),
      currentSubscription: getCurrentSubscription(state),
      permissions: (state as any).getIn(
        ['currentUser', 'data', 'permissions'],
        Map()
      ),
      roleId: (state as any).getIn(['currentUser', 'data', 'roleId']),
    }),
    isEqual
  );

  const pages = useMemo(
    () =>
      getPages({
        currentGroupId,
        features,
        groupIds,
        isCurrentGroupCouncil,
        isCurrentGroupChapter,
        isCurrentGroupOrganization,
        isCurrentGroupSystem,
        isCouncilAdmin,
        isHqAdmin,
        isSystemAdmin,
        permissions,
        roleId,
      }),
    [
      currentGroupId,
      features,
      groupIds,
      isCouncilAdmin,
      isCurrentGroupCouncil,
      isCurrentGroupChapter,
      isCurrentGroupOrganization,
      isCurrentGroupSystem,
      isHqAdmin,
      isSystemAdmin,
      permissions,
      roleId,
    ]
  );

  const shouldRenderPaddedContainer =
    pathname !== '/forms' &&
    pathname !== '/leads' &&
    pathname !== '/groups/chapters' &&
    pathname !== '/groups/organizations' &&
    pathname !== '/chapters' &&
    pathname !== '/groups/councils';

  useEffect(() => {
    if (
      currentGroupId &&
      !currentSubscription &&
      (isCurrentGroupChapter || isCurrentGroupCouncil)
    ) {
      dispatch(fetchStripeConfig());
    }
  }, [
    currentGroupId,
    currentSubscription,
    isCurrentGroupChapter,
    isCurrentGroupCouncil,
    dispatch,
  ]);

  useEffect(() => {
    if (currentGroupId) {
      dispatch(
        fetchCurrentUser({
          currentGroupId: currentGroupId,
          skipImageFetch: true,
        })
      );
    }
  }, [dispatch, currentGroupId]);

  // Inject Zoho ASAP help widget after user is authenticated
  useEffect(() => {
    if (!document.getElementById(ZOHO_SUPPORT_ID)) {
      initializeZohoSupport();
    }
  }, []);

  useEffect(() => {
    if (pathname === '/') {
      if (currentGroupId && isCurrentGroupChapter) {
        navigate(`/home${search ? '?' + search : ''}`, { replace: true });
      } else if (groupIds.size > 1 || isSystemAdmin || isHqAdmin) {
        navigate(`/groups/chapters${search ? '?' + search : ''}`, {
          replace: true,
        });
      }
    }
  }, [
    currentGroupId,
    isCurrentGroupChapter,
    isHqAdmin,
    groupIds.size,
    navigate,
    isSystemAdmin,
    pathname,
    search,
  ]);

  const queryParams = queryString.parse(search);
  const urlGroup = queryParams.group;

  useEffect(() => {
    // reinitializes the current group from any page, mainly for refreshed pages
    if (
      urlGroup &&
      (!currentGroupId || currentGroupId.toString() !== urlGroup.toString())
    ) {
      dispatch(
        setCurrentGroup({
          groupId: urlGroup,
          skipNavigate: true,
          ...(isSystemAdmin ? { getGroupByType: 5 } : {}),
        })
      );
    }
  }, [currentGroupId, dispatch, urlGroup, isSystemAdmin]);

  useEffect(() => {
    if (
      previousPathname &&
      previousPathname.includes('messages') &&
      !pathname.includes('messages')
    ) {
      dispatch(stopListeningForThreadsAction());
      dispatch(stopListeningForMessagesAction());
    }
  }, [dispatch, previousPathname, pathname]);

  return (
    <Box
      component='section'
      height='100%'
      id='appLayout'
      display='flex'
      flex='auto'
      flexDirection='column'
      minHeight='0'
      minWidth='0'
      boxSizing='border-box'
    >
      <Hidden smDown>
        <AppHeader
          isSystemAdmin={isSystemAdmin}
          pathname={pathname}
          isHqAdmin={isHqAdmin}
          isCurrentGroupOrganization={isCurrentGroupOrganization}
        />
      </Hidden>
      <Hidden mdUp>
        <AppBar position='sticky' classes={{ root: classes.appBar }}>
          <Toolbar classes={{ gutters: classes.gutters }}>
            <AppHeader
              isSystemAdmin={isSystemAdmin}
              pathname={pathname}
              isHqAdmin={isHqAdmin}
              isCurrentGroupOrganization={isCurrentGroupOrganization}
            />
          </Toolbar>
        </AppBar>
        <Routes>
          <Route
            path='/mobile-menu'
            element={<MobileMenu />}
            key='/mobile-menu'
          />
        </Routes>
      </Hidden>
      <Box
        component='section'
        display='flex'
        flex='auto'
        flexDirection='row'
        minHeight='0'
        boxSizing='border-box'
        id='appLayout'
      >
        <Hidden smDown>
          <Sidebar pages={pages} />
        </Hidden>
        <Box
          width='0'
          component='section'
          position='relative'
          flex='auto'
          minHeight='0'
          boxSizing='border-box'
          overflow='auto'
          className={classNames(
            shouldRenderPaddedContainer ? classes.paddedContainer : undefined
          )}
          id='contentContainer'
        >
          <ErrorBoundary>
            {pages.count() > 0 && (
              <Routes>
                {pages.map((page: any) => (
                  <Route
                    key={page.get('path')}
                    path={`${page.get('path')}/*`}
                    element={React.createElement(page.get('component'))}
                  />
                ))}
              </Routes>
            )}
          </ErrorBoundary>
        </Box>
      </Box>

      <Hidden mdUp>
        <MobileNavigation />
      </Hidden>
    </Box>
  );
};
ApplicationLayout.propTypes = {
  isSystemAdmin: PropTypes.bool.isRequired,
  isCouncilAdmin: PropTypes.bool.isRequired,
  isHqAdmin: PropTypes.bool.isRequired,
  isCurrentGroupChapter: PropTypes.bool.isRequired,
  isCurrentGroupOrganization: PropTypes.bool.isRequired,
  isCurrentGroupSystem: PropTypes.bool.isRequired,
};
const useStyles = makeStyles(theme => ({
  appLayout: {
    height: '100%',
  },
  logo: {
    width: '95%',
    [theme.breakpoints.only('sm')]: {
      width: '55%',
    },
    [theme.breakpoints.only('xs')]: {
      width: '85%',
    },
  },
  searchIcon: {
    textAlign: 'right',
    paddingRight: 10,
  },
  appBar: {
    backgroundColor: (theme.palette.background as any).darkPaper,
    height: 55,
  },
  appBarContainer: {
    height: 'inherit',
  },
  gutters: {
    paddingRight: 16,
    paddingLeft: 16,
  },
  paddedContainer: {
    paddingLeft: 24,
    paddingRight: 24,
  },
}));
export default ApplicationLayout;
